import React, { useEffect, useState } from 'react';
import Loader from 'react-loaders';
import { Card, CardBody, Row, Col, Input, FormGroup, Button } from 'reactstrap';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { faMobileRetro, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import createNotification from '../../../utils/createNotification';
import ToggleIcon from '../../../components/ToggleIcon/ToggleIcon';
import { useStateSelector } from '../../../store/selectors';
import {
    DigestNotificationDefaultEmailFrequency,
    DigestNotificationLabels,
    DigestNotificationTypeEnum,
    NotificationRolesEnum,
    NotificationsCategories,
    DigestNotificationCategory,
} from '../../../models/EventsDigestNotifier/DigestNotificationTypeEnum';
import { DigestRecipient } from '../../../models/EventsDigestNotifier/DigestRecipient';
import './NotificationSettings.scss';
import { MobileAppLinkModal } from './MobileAppLinkModal/MobileAppLinkModal';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import { DigestNotificationSubscriptionItem } from '../../../models/EventsDigestNotifier/DigestNotificationSubscription';
import { FrequencyPeriodEnum } from '../../../models/EventsDigestNotifier/FrequencyPeriodEnum';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { USER_ROLES } from '../../../utils/constants';
import { PRACTICE_OWNERSHIP_TYPES } from '../../../utils/constants/PracticeOwnerType.enum';
import UpgradeSubscriptionModal from '../../../components/UpgadeSubscriptionModal/UpgradeSubscriptionModal';
import { TeamMemberType } from '../../Admin/BuyerMarketplace/MyTeamMembers/Models/Enums';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';

enum AlertType {
    Email = 0,
    Push,
    Text,
    Slack,
}

interface backupNotificationSetting {
    notificationType: DigestNotificationTypeEnum;
    isEmailNotificationEnabled: boolean;
    frequencyPeriod: FrequencyPeriodEnum;
}

const frequencyLabels: Record<FrequencyPeriodEnum, string> = {
    [FrequencyPeriodEnum.PerEvent]: 'Per Event',
    [FrequencyPeriodEnum.Daily]: 'Daily',
    [FrequencyPeriodEnum.Weekly]: 'Weekly',
    [FrequencyPeriodEnum.Monthly]: 'Monthly',
    [FrequencyPeriodEnum.None]: '-',
};

interface NotificationSettingsProps {
    userId?: number;
    isReadonly?: boolean;
    notificationRoles?: NotificationRolesEnum[];
    notificationsAvailable?: boolean;
}

const NotificationSettings = ({
    userId,
    isReadonly,
    notificationRoles: notificationRolesProp,
    notificationsAvailable: notificationsAvailableProp,
}: NotificationSettingsProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [notificationsSettings, setNotificationsSettings] =
        useState<DigestRecipient>();
    const [emailSettingsBackupList, setEmailSettingsBackupList] =
        useState<backupNotificationSetting[]>();
    const [isLoadingNotificationsSettings, setIsLoadingNotificationsSettings] =
        useState(false);

    const [isSaveButtonLoading, setIsSaveButtonLoading] = useState(false);
    const [isChanged, setIsChanged] = useState(false);
    const [mobileAppLinkPopupMode, setMobileAppLinkPopupMode] = useState<
        undefined | 'AppStore' | 'GooglePlay'
    >();
    const [isMobileAppConfirmationVisible, setIsMobileAppConfirmationVisible] =
        useState(false);
    const [
        isUpgradeSubscriptionModalVisible,
        setUpgradeSubscriptionModalVisibility,
    ] = useState(false);
    const [
        areAllMobileNotificationsSelected,
        setAreAllMobileNotificationsSelected,
    ] = useState(false);
    const [
        areAllEmailNotificationsSelected,
        setAreAllEmailNotificationsSelected,
    ] = useState(false);
    const [hasActiveEngagementsState, setHasActiveEngagementsState] =
        useState(false);

    const summary = useStateSelector((s) => s.userSummary.summary);
    const limits = useStateSelector((state) => state.currentUser.limits);

    const [internalNotificationRoles, setInternalNotificationRoles] = useState<
        Array<NotificationRolesEnum>
    >([]);

    const party = useCurrentMarketplaceParty(undefined);

    // Derive notification roles from Redux summary (user-facing mode only)
    useEffect(() => {
        if (notificationRolesProp !== undefined) return;

        let roles: NotificationRolesEnum[] = [];
        const isFormerOwner =
            summary.practiceOwnerType == PRACTICE_OWNERSHIP_TYPES.FORMER;
        const isActiveOwner =
            summary.practiceOwnerType == PRACTICE_OWNERSHIP_TYPES.INDEPENDENT;
        const isCorporateOwner =
            summary.practiceOwnerType === PRACTICE_OWNERSHIP_TYPES.CORPORATE;
        const isPmgMember = summary.groups?.length > 0;
        const isPocMember = !!summary.datePocMemberSince;
        const isReferral = summary.isReferral;
        const isMarketplaceBroker = party === TimelineParty.Broker;
        const isBuyer = party === TimelineParty.Buyer;
        const isBuyerInternalTeamMember = Array.isArray(
            summary?.associateMember
        )
            ? summary.associateMember.some(
                  (m: any) =>
                      Array.isArray(m?.associateMemberMarketplaceMappings) &&
                      m.associateMemberMarketplaceMappings.some(
                          (map: any) => map?.type === TeamMemberType.Internal
                      )
              )
            : false;
        const isBuyerExternalTeamMember = Array.isArray(
            summary?.associateMember
        )
            ? summary.associateMember.some(
                  (m: any) =>
                      Array.isArray(m?.associateMemberMarketplaceMappings) &&
                      m.associateMemberMarketplaceMappings.some(
                          (map: any) => map?.type === TeamMemberType.External
                      )
              )
            : false;
        const isCharityAdmin =
            summary.roleId === USER_ROLES.CHARITY_ADMIN &&
            summary.charities?.length > 0;
        if (isActiveOwner)
            roles.push(NotificationRolesEnum.CurrentPracticeOwner);
        if (isFormerOwner)
            roles.push(NotificationRolesEnum.FormerPracticeOwner);
        if (isPmgMember) roles.push(NotificationRolesEnum.PmgMember);
        if (isPocMember) roles.push(NotificationRolesEnum.PocMember);
        if (isReferral) roles.push(NotificationRolesEnum.ReferralPartner);
        if (isMarketplaceBroker) roles.push(NotificationRolesEnum.Broker);
        if (isCharityAdmin) roles.push(NotificationRolesEnum.CharityAdmin);
        if (isCorporateOwner)
            roles.push(NotificationRolesEnum.CorporatePracticeOwner);
        if (isBuyer) roles.push(NotificationRolesEnum.Buyer);
        if (isBuyerInternalTeamMember)
            roles.push(NotificationRolesEnum.BuyerInternalTeamMember);
        if (isBuyerExternalTeamMember)
            roles.push(NotificationRolesEnum.BuyerExternalTeamMember);
        setInternalNotificationRoles(roles);
    }, [summary, notificationRolesProp]);

    const notificationRoles =
        notificationRolesProp ?? internalNotificationRoles;

    useEffect(() => {
        setIsLoadingNotificationsSettings(true);
        const url = userId
            ? `/api/DigestNotifier/${userId}`
            : '/api/DigestNotifier';
        axios
            .get(url)
            .then((response: any) => {
                if (response.data) {
                    setNotificationsSettings(response.data);
                    setEmailSettingsBackupList(
                        (
                            response.data as DigestRecipient
                        ).digestNotificationSubscriptions.map((item) => {
                            return {
                                notificationType: item.notificationType,
                                isEmailNotificationEnabled:
                                    item.isEmailNotificationEnabled,
                                frequencyPeriod: item.frequencyPeriod,
                            };
                        })
                    );
                    checkForAllNotificationsSelected(
                        response.data.digestNotificationSubscriptions
                    );
                } else {
                    createNotification(
                        response.response.data.title ??
                            'An unexpected error occurred while loading Notifications Setting.',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred while loading Notifications Setting.',
                    'error'
                );
            })
            .finally(() => setIsLoadingNotificationsSettings(false));
    }, []);

    useEffect(() => {
        if (isReadonly) return;
        if (summary && notificationRoles.length > 0) {
            checkAllEngagementsStatus();
        }
    }, [summary, notificationRoles]);

    const handleSwitchToggle = (
        notificationType: DigestNotificationTypeEnum,
        alertType: AlertType,
        eventValue: boolean
    ) => {
        const settingToUpdate =
            notificationsSettings.digestNotificationSubscriptions.find(
                (item) => item.notificationType == notificationType
            );
        var emailSettingBackupValueToUpdate: backupNotificationSetting =
            undefined;

        switch (alertType) {
            case AlertType.Email:
                if (eventValue && settingToUpdate.isPushNotificationEnabled) {
                    settingToUpdate.isPushNotificationEnabled = false;
                    let restoredFrequency = emailSettingsBackupList.find(
                        (item) =>
                            item.notificationType ==
                            settingToUpdate.notificationType
                    ).frequencyPeriod;

                    if (
                        notificationType ===
                            DigestNotificationTypeEnum.DeadlineReminder24Hours ||
                        notificationType ===
                            DigestNotificationTypeEnum.DeadlineReminderCustomDays
                    ) {
                        restoredFrequency = FrequencyPeriodEnum.PerEvent;
                    } else if (isVvConnectNotification(notificationType)) {
                        if (restoredFrequency > FrequencyPeriodEnum.Daily) {
                            restoredFrequency = FrequencyPeriodEnum.Daily;
                        }
                    }

                    settingToUpdate.frequencyPeriod = restoredFrequency;
                } else if (
                    eventValue &&
                    (notificationType ===
                        DigestNotificationTypeEnum.DeadlineReminder24Hours ||
                        notificationType ===
                            DigestNotificationTypeEnum.DeadlineReminderCustomDays)
                ) {
                    settingToUpdate.frequencyPeriod =
                        FrequencyPeriodEnum.PerEvent;
                }
                settingToUpdate.isEmailNotificationEnabled = eventValue;
                break;

            case AlertType.Push:
                if (eventValue && settingToUpdate.isEmailNotificationEnabled) {
                    emailSettingBackupValueToUpdate = {
                        notificationType: settingToUpdate.notificationType,
                        isEmailNotificationEnabled:
                            settingToUpdate.isEmailNotificationEnabled,
                        frequencyPeriod: settingToUpdate.frequencyPeriod,
                    };

                    settingToUpdate.isEmailNotificationEnabled = false;
                }
                settingToUpdate.isPushNotificationEnabled = eventValue;
                settingToUpdate.frequencyPeriod = FrequencyPeriodEnum.PerEvent;
                break;

            case AlertType.Text:
                settingToUpdate.isTextNotificationEnabled = eventValue;
                break;
            case AlertType.Slack:
                settingToUpdate.isSlackNotificationEnabled = eventValue;
                break;
        }

        if (
            notificationType ==
                DigestNotificationTypeEnum.DeadlineReminderCustomDays &&
            eventValue == false
        ) {
            if (
                alertType === AlertType.Email &&
                settingToUpdate.isPushNotificationEnabled === false
            ) {
                settingToUpdate.deadlineReminderCustomDays = null as any;
            }

            if (
                alertType === AlertType.Push &&
                settingToUpdate.isEmailNotificationEnabled === false
            ) {
                settingToUpdate.deadlineReminderCustomDays = null as any;
            }
        }

        if (emailSettingBackupValueToUpdate) {
            setEmailSettingsBackupList(
                emailSettingsBackupList.map((item) =>
                    item.notificationType ==
                    emailSettingBackupValueToUpdate.notificationType
                        ? emailSettingBackupValueToUpdate
                        : item
                )
            );
        }

        const newSettings =
            notificationsSettings.digestNotificationSubscriptions.map(
                (setting) =>
                    setting.notificationType == settingToUpdate.notificationType
                        ? settingToUpdate
                        : setting
            );

        setNotificationsSettings({
            ...notificationsSettings,
            digestNotificationSubscriptions: newSettings,
        });

        if (!isChanged) setIsChanged(true);

        checkForAllNotificationsSelected(newSettings);
    };

    const handleFrequencyChange = (
        notificationType: DigestNotificationTypeEnum,
        eventValue: number
    ) => {
        const settingToUpdate =
            notificationsSettings.digestNotificationSubscriptions.find(
                (item) => item.notificationType == notificationType
            );

        let finalFrequencyValue = eventValue;
        if (
            notificationType ===
                DigestNotificationTypeEnum.DeadlineReminder24Hours ||
            notificationType ===
                DigestNotificationTypeEnum.DeadlineReminderCustomDays
        ) {
            finalFrequencyValue = FrequencyPeriodEnum.PerEvent;
        } else if (isVvConnectNotification(notificationType)) {
            if (eventValue > FrequencyPeriodEnum.Daily) {
                finalFrequencyValue = FrequencyPeriodEnum.Daily;
            }
        }

        settingToUpdate.frequencyPeriod = finalFrequencyValue;

        const newSettings =
            notificationsSettings.digestNotificationSubscriptions.map(
                (setting) => {
                    return setting.notificationType ==
                        settingToUpdate.notificationType
                        ? settingToUpdate
                        : setting;
                }
            );

        setNotificationsSettings({
            ...notificationsSettings,
            digestNotificationSubscriptions: newSettings,
        });

        setEmailSettingsBackupList(
            emailSettingsBackupList.map((item) =>
                item.notificationType == notificationType
                    ? {
                          notificationType: notificationType,
                          isEmailNotificationEnabled:
                              item.isEmailNotificationEnabled,
                          frequencyPeriod: finalFrequencyValue,
                      }
                    : item
            )
        );

        if (!isChanged) setIsChanged(true);
    };

    const handleDeadlineReminderCustomDaysChange = (
        notificationType: DigestNotificationTypeEnum,
        days: number | null
    ) => {
        const settingToUpdate =
            notificationsSettings.digestNotificationSubscriptions.find(
                (item) => item.notificationType == notificationType
            );

        settingToUpdate.deadlineReminderCustomDays = days as any;

        const newSettings =
            notificationsSettings.digestNotificationSubscriptions.map(
                (setting) => {
                    return setting.notificationType ==
                        settingToUpdate.notificationType
                        ? settingToUpdate
                        : setting;
                }
            );

        setNotificationsSettings({
            ...notificationsSettings,
            digestNotificationSubscriptions: newSettings,
        });

        if (!isChanged) setIsChanged(true);
    };

    const handleMobileCheckChange = () => {
        if (notificationsSettings) {
            const newMobileAppValue =
                !notificationsSettings.isPushNotificationsAvailable;

            const newSubscriptionSettingsState =
                notificationsSettings.digestNotificationSubscriptions;

            if (newMobileAppValue) {
                setEmailSettingsBackupList(
                    notificationsSettings.digestNotificationSubscriptions.map(
                        (item) => {
                            return {
                                notificationType: item.notificationType,
                                isEmailNotificationEnabled:
                                    item.isEmailNotificationEnabled,
                                frequencyPeriod: item.frequencyPeriod,
                            };
                        }
                    )
                );

                newSubscriptionSettingsState.forEach((setting) => {
                    const isDailyEvent =
                        setting.notificationType ==
                            DigestNotificationTypeEnum.WalletUpdated ||
                        setting.notificationType ==
                            DigestNotificationTypeEnum.NewKnowledgeLibraryItem ||
                        setting.notificationType ==
                            DigestNotificationTypeEnum.NewPocPost;

                    setting.frequencyPeriod = isDailyEvent
                        ? FrequencyPeriodEnum.Daily
                        : FrequencyPeriodEnum.PerEvent;
                    setting.isEmailNotificationEnabled = false;
                    setting.isPushNotificationEnabled = true;
                });
            } else {
                newSubscriptionSettingsState.forEach((setting) => {
                    if (setting.isPushNotificationEnabled) {
                        const backupValue = emailSettingsBackupList.find(
                            (notif) =>
                                notif.notificationType ==
                                setting.notificationType
                        );

                        let restoredFrequency = backupValue.frequencyPeriod;

                        if (isVvConnectNotification(setting.notificationType)) {
                            if (restoredFrequency > FrequencyPeriodEnum.Daily) {
                                restoredFrequency = FrequencyPeriodEnum.Daily;
                            }
                        }

                        setting.isEmailNotificationEnabled =
                            backupValue.isEmailNotificationEnabled;
                        setting.isPushNotificationEnabled = false;
                        setting.frequencyPeriod = restoredFrequency;
                    }
                });
            }

            setNotificationsSettings({
                ...notificationsSettings,
                isPushNotificationsAvailable: newMobileAppValue,
                digestNotificationSubscriptions: newSubscriptionSettingsState,
            });

            if (!isChanged) setIsChanged(true);
            checkForAllNotificationsSelected(newSubscriptionSettingsState);
        }
    };

    const selectAllMobileHandler = () => {
        const areAllMobileSet =
            notificationsSettings.digestNotificationSubscriptions.every(
                (d) => d.isPushNotificationEnabled
            );
        let updatedSubscriptions = [];
        if (areAllMobileSet) {
            updatedSubscriptions =
                notificationsSettings.digestNotificationSubscriptions.map(
                    (d) => {
                        return {
                            ...d,
                            deadlineReminderCustomDays: null as any,
                            isPushNotificationEnabled: false,
                        };
                    }
                );
        } else {
            updatedSubscriptions =
                notificationsSettings.digestNotificationSubscriptions.map(
                    (d) => {
                        return {
                            ...d,
                            isPushNotificationEnabled: true,
                            isEmailNotificationEnabled: false,
                            frequencyPeriod: FrequencyPeriodEnum.PerEvent,
                        };
                    }
                );
        }
        setNotificationsSettings({
            ...notificationsSettings,
            digestNotificationSubscriptions: updatedSubscriptions,
        });
        setAreAllMobileNotificationsSelected(!areAllMobileSet);
        setAreAllEmailNotificationsSelected(false);
        if (!isChanged) setIsChanged(true);
    };

    const selectAllEmailHandler = () => {
        const areAllEmailSet =
            notificationsSettings.digestNotificationSubscriptions.every(
                (d) => d.isEmailNotificationEnabled
            );
        let updatedSubscriptions = [];
        if (areAllEmailSet) {
            updatedSubscriptions =
                notificationsSettings.digestNotificationSubscriptions.map(
                    (d) => {
                        return {
                            ...d,
                            deadlineReminderCustomDays: null as any,
                            isEmailNotificationEnabled: false,
                        };
                    }
                );
        } else {
            updatedSubscriptions =
                notificationsSettings.digestNotificationSubscriptions.map(
                    (d) => {
                        if (d.isEmailNotificationEnabled) return d;

                        let defaultFrequency =
                            DigestNotificationDefaultEmailFrequency.find(
                                (f) => d.notificationType == f.id
                            )?.frequency ?? FrequencyPeriodEnum.PerEvent;

                        if (isVvConnectNotification(d.notificationType)) {
                            if (defaultFrequency > FrequencyPeriodEnum.Daily) {
                                defaultFrequency = FrequencyPeriodEnum.Daily;
                            }
                        }

                        return {
                            ...d,
                            isPushNotificationEnabled: false,
                            isEmailNotificationEnabled: true,
                            frequencyPeriod: defaultFrequency,
                        };
                    }
                );
        }
        setNotificationsSettings({
            ...notificationsSettings,
            digestNotificationSubscriptions: updatedSubscriptions,
        });
        setAreAllEmailNotificationsSelected(!areAllEmailSet);
        setAreAllMobileNotificationsSelected(false);
        if (!isChanged) setIsChanged(true);
    };

    const checkForAllNotificationsSelected = (settings: any[]) => {
        const areAllMobileSet = settings.every(
            (d) => d.isPushNotificationEnabled
        );
        setAreAllMobileNotificationsSelected(areAllMobileSet);

        const areAllEmailSet = settings.every(
            (d) => d.isEmailNotificationEnabled
        );
        setAreAllEmailNotificationsSelected(areAllEmailSet);
    };

    const handleSave = () => {
        setIsSaveButtonLoading(true);
        axios
            .put('/api/DigestNotifier', {
                userId: notificationsSettings.userId,
                isPushNotificationsAvailable:
                    notificationsSettings.isPushNotificationsAvailable,
                notificationSubscriptions:
                    notificationsSettings.digestNotificationSubscriptions,
            })
            .then((response: any) => {
                if (response && response.status == 200) {
                    createNotification(
                        'Notifications Setting saved successfully.',
                        'success'
                    );

                    setEmailSettingsBackupList(
                        notificationsSettings.digestNotificationSubscriptions.map(
                            (item) => {
                                return {
                                    notificationType: item.notificationType,
                                    isEmailNotificationEnabled:
                                        item.isEmailNotificationEnabled,
                                    frequencyPeriod: item.frequencyPeriod,
                                };
                            }
                        )
                    );
                    setIsChanged(false);
                } else {
                    createNotification(
                        response.response.data.title ??
                            'An unexpected error occurred while saving Notifications Setting.',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred while saving Notifications Setting.',
                    'error'
                );
            })
            .finally(() => {
                setIsSaveButtonLoading(false);
            });
    };

    const renderDeadlineInput = (
        notifiationItem: DigestNotificationSubscriptionItem
    ) => {
        const isDisabled =
            (!notifiationItem.isEmailNotificationEnabled &&
                !notifiationItem.isPushNotificationEnabled) ||
            !areNotificationsAvailable();
        const currentValue = notifiationItem.deadlineReminderCustomDays;
        const deadlineInvalid =
            !isDisabled &&
            (currentValue == null || currentValue < 2 || currentValue > 14);

        if (isReadonly) {
            return (
                <div className="activity-input">
                    <strong>{currentValue ?? '-'}</strong>
                </div>
            );
        }

        return (
            <div className="activity-input">
                <input
                    value={currentValue ?? ''}
                    disabled={isDisabled}
                    min={2}
                    max={14}
                    required={!isDisabled}
                    type="number"
                    className={
                        'form-control' + (deadlineInvalid ? ' is-invalid' : '')
                    }
                    onChange={(event) => {
                        const raw = event.target.value;
                        const val = raw === '' ? null : Number(raw);

                        if (val === null) {
                            handleDeadlineReminderCustomDaysChange(
                                notifiationItem.notificationType,
                                null
                            );
                        } else {
                            handleDeadlineReminderCustomDaysChange(
                                notifiationItem.notificationType,
                                event.target.valueAsNumber
                            );
                        }
                    }}
                    aria-label="activity-input"
                />
            </div>
        );
    };

    const renderNotifiationEventRow = (
        notifiationItem: DigestNotificationSubscriptionItem,
        index: number
    ) => {
        const displayFrequency =
            notifiationItem.notificationType ===
                DigestNotificationTypeEnum.DeadlineReminder24Hours ||
            notifiationItem.notificationType ===
                DigestNotificationTypeEnum.DeadlineReminderCustomDays
                ? FrequencyPeriodEnum.PerEvent
                : notifiationItem.frequencyPeriod;

        return (
            <Row
                className={
                    'events-row' +
                    (notifiationItem.isUnavailable
                        ? ' row-unavailable'
                        : !notifiationItem.isEmailNotificationEnabled &&
                            !notifiationItem.isPushNotificationEnabled
                          ? ' row-disabled'
                          : '')
                }
                key={`events-row-${index}`}>
                <Col md={12}>
                    <div className="row-container-wrap">
                        <div className="content-row-container">
                            {notificationsSettings.isPushNotificationsAvailable ? (
                                <div className="toggle-icon-container-container flex">
                                    <ToggleIcon
                                        disabled={!areNotificationsAvailable()}
                                        icon={faMobileRetro}
                                        size="lg"
                                        idText={`toggle-push-${index}`}
                                        key={`toggle-push-${index}`}
                                        checked={
                                            notifiationItem.isPushNotificationEnabled
                                        }
                                        onChange={
                                            isReadonly
                                                ? () => {}
                                                : (newState) =>
                                                      handleSwitchToggle(
                                                          notifiationItem.notificationType,
                                                          AlertType.Push,
                                                          newState
                                                      )
                                        }
                                    />
                                </div>
                            ) : (
                                []
                            )}

                            <div className="toggle-icon-container-container flex">
                                <ToggleIcon
                                    disabled={!areNotificationsAvailable()}
                                    icon={faEnvelope}
                                    size="lg"
                                    idText={`toggle-email-${index}`}
                                    key={`toggle-email-${index}`}
                                    checked={
                                        notifiationItem.isEmailNotificationEnabled
                                    }
                                    onChange={
                                        isReadonly
                                            ? () => {}
                                            : (newState) =>
                                                  handleSwitchToggle(
                                                      notifiationItem.notificationType,
                                                      AlertType.Email,
                                                      newState
                                                  )
                                    }
                                />
                            </div>

                            <div className="flex frequency-label">
                                {isReadonly ? (
                                    <strong>
                                        {frequencyLabels[displayFrequency] ??
                                            '-'}
                                    </strong>
                                ) : (
                                    <select
                                        value={displayFrequency}
                                        onChange={(event) =>
                                            handleFrequencyChange(
                                                notifiationItem.notificationType,
                                                event.target.selectedIndex
                                            )
                                        }
                                        disabled={
                                            !notifiationItem.isEmailNotificationEnabled ||
                                            !areNotificationsAvailable()
                                        }
                                        className="form-control">
                                        <option value={'0'}>Per Event</option>
                                        {!(
                                            notifiationItem.notificationType ===
                                                DigestNotificationTypeEnum.DeadlineReminder24Hours ||
                                            notifiationItem.notificationType ===
                                                DigestNotificationTypeEnum.DeadlineReminderCustomDays
                                        ) && (
                                            <>
                                                <option
                                                    value={'1'}
                                                    disabled={
                                                        notifiationItem.isPushNotificationEnabled
                                                    }>
                                                    Daily
                                                </option>
                                                {!isVvConnectNotification(
                                                    notifiationItem.notificationType
                                                ) && (
                                                    <>
                                                        <option
                                                            value={'2'}
                                                            disabled={
                                                                notifiationItem.isPushNotificationEnabled
                                                            }>
                                                            Weekly
                                                        </option>
                                                        <option
                                                            value={'3'}
                                                            disabled={
                                                                notifiationItem.isPushNotificationEnabled
                                                            }>
                                                            Monthly
                                                        </option>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </select>
                                )}
                            </div>
                        </div>
                        <div className="activity-row">
                            <div className="activity-label">
                                {!isReadonly &&
                                summary.practiceOwnerType ===
                                    PRACTICE_OWNERSHIP_TYPES.CORPORATE &&
                                notifiationItem.notificationType ===
                                    DigestNotificationTypeEnum.NewPocPost ? (
                                    <div>
                                        New {summary.corporateAffiliation.name}{' '}
                                        Community post
                                    </div>
                                ) : (
                                    (DigestNotificationLabels.find(
                                        (type) =>
                                            type.id ==
                                            notifiationItem.notificationType
                                    )?.name ?? '-')
                                )}
                            </div>
                            {notifiationItem.notificationType ===
                            DigestNotificationTypeEnum.DeadlineReminderCustomDays ? (
                                <>
                                    {isReadonly &&
                                    !notifiationItem.deadlineReminderCustomDays ? (
                                        <strong>N/A</strong>
                                    ) : (
                                        renderDeadlineInput(notifiationItem)
                                    )}
                                    <div>Days</div>
                                </>
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
        );
    };

    const renderLoader = () => {
        return (
            <div className="loader-container">
                <div className="loader-container-inner">
                    <div className="text-center">
                        <Loader
                            active={isLoadingNotificationsSettings}
                            type="ball-scale-multiple"
                        />
                    </div>
                    <h6 className="mt-5">Loading...</h6>
                </div>
            </div>
        );
    };

    const areNotificationsAvailable = () => {
        if (notificationsAvailableProp !== undefined)
            return notificationsAvailableProp;
        return limits.currentSubscriptionLimitsState.doesNotificationsAvailable;
    };

    const checkEngagementStatus = async (
        engagementId: number
    ): Promise<boolean> => {
        try {
            const response = await axios.get<boolean>(
                `/api/Marketplace/Engagements/${engagementId}/IsActive`
            );

            if (response.status === 200) {
                return response.data ?? false;
            }
            return false;
        } catch (error) {
            return false;
        }
    };

    const checkAllEngagementsStatus = async () => {
        const engagements = summary?.engagements || [];
        if (engagements.length === 0) {
            setHasActiveEngagementsState(false);
            return;
        }

        try {
            const statusChecks = engagements.map((engagement: any) =>
                checkEngagementStatus(engagement.id)
            );
            const results = await Promise.all(statusChecks);
            const hasActive = results.some((isActive) => isActive);
            setHasActiveEngagementsState(hasActive);
        } catch (error) {
            setHasActiveEngagementsState(false);
        }
    };

    const getVisibleCategories = () => {
        // In readonly mode, skip the async engagement check and treat as active
        const effectiveHasActiveEngagements = isReadonly
            ? true
            : hasActiveEngagementsState;

        const isBroker = notificationRoles.includes(
            NotificationRolesEnum.Broker
        );
        const isCurrentOwner = notificationRoles.includes(
            NotificationRolesEnum.CurrentPracticeOwner
        );
        const isBuyer = notificationRoles.includes(NotificationRolesEnum.Buyer);
        const isBuyerInternalMember = notificationRoles.includes(
            NotificationRolesEnum.BuyerInternalTeamMember
        );
        const isBuyerExternalMember = notificationRoles.includes(
            NotificationRolesEnum.BuyerExternalTeamMember
        );

        let categories = NotificationsCategories;

        if (isCurrentOwner && !isBroker) {
            if (!effectiveHasActiveEngagements) {
                categories = NotificationsCategories.filter(
                    (cat) =>
                        cat.category !== DigestNotificationCategory.VvConnect
                );
            }
        } else if (
            !isBroker &&
            !(
                isCurrentOwner ||
                isBuyer ||
                isBuyerInternalMember ||
                isBuyerExternalMember
            )
        ) {
            categories = NotificationsCategories.filter(
                (cat) => cat.category !== DigestNotificationCategory.VvConnect
            );
        }

        const filteredCategories = categories.filter((category) =>
            notificationsSettings.digestNotificationSubscriptions.some(
                (subscription) =>
                    category.notifications.some(
                        (notif) => notif.type === subscription.notificationType
                    )
            )
        );
        return filteredCategories;
    };

    const isVvConnectNotification = (
        notificationType: DigestNotificationTypeEnum
    ): boolean => {
        return NotificationsCategories.some(
            (category) =>
                category.category === DigestNotificationCategory.VvConnect &&
                category.notifications.some((n) => n.type === notificationType)
        );
    };

    const sortSubscriptionsByRole = (
        list: DigestNotificationSubscriptionItem[]
    ) => {
        if (!notificationRoles || notificationRoles.length === 0)
            return list.sort((a, b) => a.notificationType - b.notificationType);

        const flattenedDescriptors = NotificationsCategories.reduce(
            (acc: any[], cat) => acc.concat(cat.notifications),
            []
        );

        const getOrderValue = (item: DigestNotificationSubscriptionItem) => {
            const descriptor: any = flattenedDescriptors.find(
                (d) => d.type === item.notificationType
            );

            if (descriptor && descriptor.order) {
                const orders: number[] = [];
                for (const role of notificationRoles) {
                    const val = descriptor.order[role as any];
                    if (typeof val === 'number') orders.push(val);
                }
                if (orders.length > 0) return Math.min(...orders);
            }

            return item.notificationType;
        };

        return [...list].sort((a, b) => {
            const oa = getOrderValue(a);
            const ob = getOrderValue(b);
            if (oa === ob) return a.notificationType - b.notificationType;
            return oa - ob;
        });
    };

    const hasInvalidDeadlineInputs = () => {
        if (!notificationsSettings) return false;

        return notificationsSettings.digestNotificationSubscriptions.some(
            (s) =>
                s.notificationType ===
                    DigestNotificationTypeEnum.DeadlineReminderCustomDays &&
                (s.isEmailNotificationEnabled || s.isPushNotificationEnabled) &&
                areNotificationsAvailable() &&
                (s.deadlineReminderCustomDays == null ||
                    s.deadlineReminderCustomDays < 2 ||
                    s.deadlineReminderCustomDays > 14)
        );
    };

    if (isLoadingNotificationsSettings) {
        return <div className="notifications-page">{renderLoader()}</div>;
    }

    return (
        <div className="notifications-page">
            <Card className="card-section main-card mb-3">
                <CardBody>
                    {!isReadonly && (
                        <>
                            <p>
                                <p className="no-margin">
                                    Members are notified of relevant EcoSystem
                                    activity as shown below. Subscribers are
                                    allowed to customize their notifications in
                                    three dimensions via{' '}
                                    <span className="no-wrap">
                                        email{' '}
                                        {
                                            <FontAwesomeIcon
                                                icon={faEnvelope}
                                                className="notify-text-icon"></FontAwesomeIcon>
                                        }
                                    </span>{' '}
                                    or
                                    <span className="no-wrap">
                                        {' '}
                                        mobile{' '}
                                        {
                                            <FontAwesomeIcon
                                                icon={faMobileRetro}
                                                className="notify-text-icon"></FontAwesomeIcon>
                                        }{' '}
                                    </span>
                                    notification{' '}
                                    {limits.isUpgradeAvailable && (
                                        <Button
                                            className="btn btn-sm btn-primary mb-1"
                                            onClick={() => {
                                                setUpgradeSubscriptionModalVisibility(
                                                    true
                                                );
                                            }}>
                                            Upgrade to Subscriber
                                        </Button>
                                    )}
                                </p>
                                <ul className="ul_inner">
                                    <li> Activity Type </li>
                                    <li> Notification Type </li>
                                    <li> Notification Frequency </li>
                                </ul>
                                <br />
                                We recommend using your mobile device to receive
                                notifications on a per-event basis. If you elect
                                to receive per event notifications via email, a
                                limit of 4 emails notifications per day will
                                apply. All other notifications will be
                                summarized in a daily roundup email.
                                <br />
                            </p>
                            <p>
                                Mobile device notification is available to those
                                have installed the free VetValue EcoSystem
                                mobile application, available for download in
                                the{' '}
                                <a
                                    className="store-link"
                                    onClick={() =>
                                        setMobileAppLinkPopupMode('AppStore')
                                    }>
                                    App Store
                                </a>{' '}
                                and{' '}
                                <a
                                    className="store-link"
                                    onClick={() =>
                                        setMobileAppLinkPopupMode('GooglePlay')
                                    }>
                                    Google Play
                                </a>
                                , and have not blocked notifications on their
                                device.
                            </p>
                        </>
                    )}

                    <FormGroup check className="mobile-app-check">
                        {isReadonly ? (
                            <strong>
                                Has mobile app:{' '}
                                {notificationsSettings?.isPushNotificationsAvailable
                                    ? 'Yes'
                                    : 'No'}
                            </strong>
                        ) : (
                            <label className="checkbox-label">
                                <Input
                                    disabled={!areNotificationsAvailable()}
                                    checked={
                                        notificationsSettings?.isPushNotificationsAvailable
                                    }
                                    type="checkbox"
                                    onChange={() =>
                                        setIsMobileAppConfirmationVisible(true)
                                    }
                                    className="checkbox-input"
                                />
                                <span className="wallet-text-ref">
                                    Confirm you have downloaded the mobile
                                    application and have not blocked
                                    notifications on your phone.
                                </span>
                            </label>
                        )}
                    </FormGroup>

                    {!isReadonly && (
                        <p>
                            Members may adjust the preset notification settings
                            (based on mobile application download status) below
                            at any time. A monthly email round up will notify
                            you of anything not selected below.
                        </p>
                    )}

                    {notificationsSettings && (
                        <div className="rows-container">
                            <Row className="events-row events-header">
                                <Col md={12}>
                                    <div className="table-header-container">
                                        {notificationsSettings.isPushNotificationsAvailable ? (
                                            isReadonly ? (
                                                <div className="select-btn" />
                                            ) : (
                                                <Button
                                                    disabled={
                                                        !areNotificationsAvailable()
                                                    }
                                                    className="btn btn-primary select-btn"
                                                    onClick={
                                                        selectAllMobileHandler
                                                    }>
                                                    {areAllMobileNotificationsSelected
                                                        ? 'Deselect'
                                                        : 'Select'}{' '}
                                                    All
                                                    <FontAwesomeIcon
                                                        icon={faMobileRetro}
                                                        className="select-icon"></FontAwesomeIcon>
                                                </Button>
                                            )
                                        ) : (
                                            []
                                        )}
                                        {isReadonly ? (
                                            <div className="select-btn" />
                                        ) : (
                                            <Button
                                                disabled={
                                                    !areNotificationsAvailable()
                                                }
                                                className="btn btn-primary select-btn"
                                                onClick={selectAllEmailHandler}>
                                                {areAllEmailNotificationsSelected
                                                    ? 'Deselect'
                                                    : 'Select'}{' '}
                                                All
                                                <FontAwesomeIcon
                                                    icon={faEnvelope}
                                                    className="select-icon"></FontAwesomeIcon>
                                            </Button>
                                        )}
                                        <div className="text-align-center frequency-label">
                                            Frequency
                                        </div>
                                        <div className="text-align-center">
                                            Activity Type
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            {getVisibleCategories().map((category, index) => (
                                <>
                                    <h6
                                        className={
                                            index === 0
                                                ? 'category-label-first'
                                                : 'category-label'
                                        }>
                                        {category.label}
                                    </h6>
                                    {sortSubscriptionsByRole(
                                        notificationsSettings.digestNotificationSubscriptions.filter(
                                            (s) =>
                                                category.notifications.some(
                                                    (n) =>
                                                        n.type ==
                                                        s.notificationType
                                                )
                                        )
                                    ).map(
                                        (
                                            notifItem: DigestNotificationSubscriptionItem,
                                            index: number
                                        ) =>
                                            renderNotifiationEventRow(
                                                notifItem,
                                                index
                                            )
                                    )}
                                </>
                            ))}
                        </div>
                    )}

                    {!isReadonly && (
                        <ButtonLoader
                            buttonText={'Save'}
                            loaderButtonText={''}
                            disabled={
                                !notificationsSettings ||
                                !isChanged ||
                                !areNotificationsAvailable() ||
                                hasInvalidDeadlineInputs()
                            }
                            isLoading={isSaveButtonLoading}
                            onClick={handleSave}
                            className="btn btn-primary save-btn"></ButtonLoader>
                    )}
                </CardBody>
            </Card>

            {!isReadonly && mobileAppLinkPopupMode ? (
                <MobileAppLinkModal
                    type={mobileAppLinkPopupMode}
                    close={() =>
                        setMobileAppLinkPopupMode(undefined)
                    }></MobileAppLinkModal>
            ) : (
                <></>
            )}
            {!isReadonly && isUpgradeSubscriptionModalVisible ? (
                <UpgradeSubscriptionModal
                    onClose={() => {
                        setUpgradeSubscriptionModalVisibility(false);
                    }}
                    isVisible={isUpgradeSubscriptionModalVisible}
                    ownerType={summary.practiceOwnerType}
                />
            ) : (
                <></>
            )}
            {!isReadonly && isMobileAppConfirmationVisible ? (
                <div className="mobile-app-confirmation">
                    <ConfirmationModal
                        isVisible={isMobileAppConfirmationVisible}
                        setIsVisible={setIsMobileAppConfirmationVisible}
                        header="Are you sure?"
                        confirmationText={
                            notificationsSettings?.isPushNotificationsAvailable
                                ? "Confirm you don't have downloaded the mobile application"
                                : 'Confirm you have downloaded the mobile application'
                        }
                        onConfirm={() => handleMobileCheckChange()}
                        closeOnConfirm={true}
                        params={undefined}
                        nextButtonText="Yes, I'm sure"
                        cancelButtonText="No, take me back"></ConfirmationModal>
                </div>
            ) : (
                <></>
            )}
        </div>
    );
};

export default NotificationSettings;
